import { useState } from 'react';
import { FormSpy } from 'react-final-form';
import { useDispatch } from 'react-redux';
import type { DocumentUpsertModel } from 'api/models';
import { StepFormLayout } from 'accounts/components/step-form-layout';
import { HOME_LICENSES_RI_STRINGS as STRINGS } from 'accounts/constants/strings/home-licenses-ri.strings';
import { useSetupFlowStep } from 'accounts/hooks/homeowners/use-setup-flow-step-context';
import { DateInputFinalFormAdapter } from 'components/DateInput/DateInput';
import Field from 'components/Field/Field';
import { InputFinalFormAdapter } from 'components/Input/Input';
import { maskMMDDYYYY, unmaskMMDDYYYY } from 'components/Input/masks';
import {
  MEDIA_LABEL,
  NewMediaUploadAdapter,
  validateMedia,
} from 'components/NewMediaUploader/NewMediaUploader';
import {
  SELECTOR_TYPES,
  SelectorFinalFormAdapter,
} from 'components/Selector/Selector';
import { Col, Row } from 'forkedlibraries/react-bootstrap';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { IMAGE_FILE_TYPE } from 'models/enums/index';
import { uploadFiles } from 'store/redux/images/actions';
import { getString } from 'strings/index';
import {
  composeValidators,
  dateValidationMMDDYY,
  required,
  validPastOrTodayMMDDYYDate,
} from 'utils/validation';
import {
  formatExpirationDate,
  getExpirationDate,
  isExpired,
  RI_CERTIFICATE_TYPE,
  RICertificateType,
  RILicensesFormValues,
  toRILicensesFormValues,
  toRILicensesUpdatePayload,
} from './home-licenses-ri.utils';

/**
 * The step's one blocker. Like DC's unregistered home, it is a form-level error rather than a field
 * one: it lives under `keys` (not a real field path) so no input gets highlighted, and `customError`
 * promotes the message into the submit toast. The red box under the selector is the on-page
 * explanation.
 *
 * A lapsed Certificate of Lead Conformance is not listed here — the backend rejects it outright, and
 * the on-page note under the date tells the homeowner why before they try to save.
 */
function validateLicenses(values: Record<string, unknown>) {
  if (values?.certificateType === RI_CERTIFICATE_TYPE.NONE) {
    return { keys: { certificateType: STRINGS['none.error'] } };
  }

  return {};
}

function InfoNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-lg rounded border border-solid border-green bg-green-translucent-light p-sm text-body text-navy">
      {children}
    </div>
  );
}

function WarningNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-lg rounded border border-solid border-red bg-red-translucent-light p-sm text-body text-navy">
      {children}
    </div>
  );
}

/**
 * Rhode Island variant of the Licenses step, rendered by the dispatcher when the home's market is RI.
 *
 * The rule is statewide but keyed on year built, and that year is confirmed in the Disclosures step,
 * which runs before this one. So the step always shows and takes the exemption from the envelope
 * rather than asking for the year a second time.
 */
export function HomeLicensesRI() {
  const dispatch = useDispatch();
  const { currentStepFormData, goPreviousStep, handleSaveAndNext, setLoading } =
    useSetupFlowStep()!;

  // The progress bar is rendered by `StepFormLayout` above the form, so the answer that gates forward
  // navigation has to be mirrored outside of it. Setting the same value is a no-op in React, so this
  // costs no extra render per keystroke.
  const [certificateType, setCertificateType] = useState<
    RICertificateType | undefined
  >();

  // Resolved server-side from the year the homeowner confirms in Disclosures, which runs before this
  // step. Defaults to requiring the certificate, so a year that never arrived cannot waive it.
  const exempt = currentStepFormData?.ri?.leadCertificateRequired === false;
  const blocked = !exempt && certificateType === RI_CERTIFICATE_TYPE.NONE;

  // `uploadFiles` is a redux thunk; the loosely-typed `dispatch` widens its return to the thunk
  // itself, so cast to the resolved upload descriptors.
  async function uploadMedia(media: unknown) {
    const result = (await dispatch(
      uploadFiles(
        IMAGE_FILE_TYPE.LicenseLeadCertificateRI,
        media as Record<string, unknown>,
      ),
    )) as unknown as DocumentUpsertModel[] | undefined;
    return result?.length ? result[0] : undefined;
  }

  async function handleSubmit(values: RILicensesFormValues) {
    const capturesCertificate =
      !exempt && values.certificateType !== RI_CERTIFICATE_TYPE.NONE;

    let file: DocumentUpsertModel | undefined;

    if (capturesCertificate && values.file) {
      setLoading?.(true);
      try {
        file = await uploadMedia(values.file);
      } catch (err) {
        console.error(err);
        setLoading?.(false);
        return;
      }
      setLoading?.(false);
    }

    await handleSaveAndNext?.(toRILicensesUpdatePayload(values, { file }));
  }

  return (
    <StepFormLayout
      onSubmit={handleSubmit}
      handleBackStep={goPreviousStep}
      title={STRINGS.title}
      subTitle={STRINGS.subtitle}
      initialValues={toRILicensesFormValues(currentStepFormData)}
      customValidation={validateLicenses}
      customError="certificateType"
      blockForwardProgress={blocked}
      getForm={({ values, form }: any) => {
        const expiration = getExpirationDate(
          values?.certificateType,
          values?.issuanceDate,
        );
        const expired = isExpired(expiration);

        return (
          <>
            <FormSpy
              subscription={{ values: true }}
              onChange={({ values: formValues }) =>
                setCertificateType(formValues?.certificateType)
              }
            />

            {exempt ? (
              <FormLayout.Section
                firstSection
                sectionTitle={STRINGS['exempt.title']}
              >
                <div className="rounded border border-solid border-gray bg-light-gray p-sm text-body text-navy">
                  {STRINGS['exempt.subtitle']}
                </div>
                <p className="mt-sm text-p1 text-dark-gray">
                  {STRINGS['exempt.other_exemptions']}
                </p>
              </FormLayout.Section>
            ) : (
              <FormLayout.Section
                firstSection
                sectionTitle={STRINGS['certificate.title']}
                sectionSubTitle={STRINGS['certificate.subtitle']}
              >
                <Field
                  name="certificateType"
                  component={SelectorFinalFormAdapter}
                  validate={required}
                  fluid
                  type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
                  className="mt-sm"
                  // The number, the date and the document describe one specific certificate, and both
                  // options render the same three fields — so without this, switching from Lead
                  // Conformance to Lead-Free would submit the first certificate's details filed under
                  // the second's type.
                  onChangeCustom={() => {
                    form.change('certificateNumber', undefined);
                    form.change('issuanceDate', undefined);
                    form.change('file', undefined);
                  }}
                  buttons={[
                    {
                      label: STRINGS['certificate.conformance_label'],
                      subLabel: STRINGS['certificate.conformance_sub_label'],
                      key: RI_CERTIFICATE_TYPE.CONFORMANCE,
                      centerText: true,
                    },
                    {
                      label: STRINGS['certificate.lead_free_label'],
                      key: RI_CERTIFICATE_TYPE.LEAD_FREE,
                      centerText: true,
                    },
                    {
                      label: STRINGS['certificate.none_label'],
                      key: RI_CERTIFICATE_TYPE.NONE,
                      centerText: true,
                    },
                  ]}
                />

                {/* Both certificates capture the same three things; only the wording and the
                    two-year expiry differ. */}
                {(values?.certificateType === RI_CERTIFICATE_TYPE.CONFORMANCE ||
                  values?.certificateType ===
                    RI_CERTIFICATE_TYPE.LEAD_FREE) && (
                  <>
                    <h4 className="mt-xl text-h4">
                      {values.certificateType === RI_CERTIFICATE_TYPE.LEAD_FREE
                        ? STRINGS['lead_free.title']
                        : STRINGS['conformance.title']}
                    </h4>
                    <p className="mt-xs text-p1 text-dark-gray">
                      {values.certificateType === RI_CERTIFICATE_TYPE.LEAD_FREE
                        ? STRINGS['lead_free.subtitle']
                        : STRINGS['conformance.subtitle']}
                    </p>

                    <Row className="mt-lg">
                      <Col xs={24} md={6}>
                        <Field
                          name="certificateNumber"
                          component={InputFinalFormAdapter}
                          placeholder={STRINGS['certificate.number_label']}
                          validate={required}
                        />
                      </Col>
                      <Col xs={24} md={6}>
                        <Field
                          name="issuanceDate"
                          component={DateInputFinalFormAdapter}
                          mask={maskMMDDYYYY}
                          unmask={unmaskMMDDYYYY}
                          placeholder={STRINGS['certificate.issuance_label']}
                          validate={composeValidators(
                            required,
                            dateValidationMMDDYY,
                            validPastOrTodayMMDDYYDate,
                          )}
                        />
                      </Col>
                    </Row>

                    <div className="mt-lg">
                      <Field
                        name="file"
                        validate={validateMedia}
                        component={NewMediaUploadAdapter}
                        mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
                        customLabel={
                          values.certificateType ===
                          RI_CERTIFICATE_TYPE.LEAD_FREE
                            ? STRINGS['lead_free.upload_label']
                            : STRINGS['conformance.upload_label']
                        }
                        disableWebcam
                        cleanOnMediaChange
                        isSingleMedia
                        ignoreError={false}
                        size="fluid"
                      />
                    </div>

                    {/* The Lead-Free certificate never expires, so `expiration` is only set for a
                        Conformance one. */}
                    {expiration &&
                      (expired ? (
                        <WarningNote>
                          {getString(STRINGS['conformance.expired'], {
                            date: formatExpirationDate(expiration),
                          })}
                        </WarningNote>
                      ) : (
                        <InfoNote>
                          {getString(STRINGS['conformance.valid_until'], {
                            date: formatExpirationDate(expiration),
                          })}
                        </InfoNote>
                      ))}
                  </>
                )}

                {values?.certificateType === RI_CERTIFICATE_TYPE.NONE && (
                  <WarningNote>{STRINGS['none.warning']}</WarningNote>
                )}
              </FormLayout.Section>
            )}
          </>
        );
      }}
    />
  );
}
